/**
 * 3. Email Verification — verifies the work email before tenant/workspace
 * creation proceeds.
 *
 * Arabic and English (C7). The outcome is kept as a state and an error, not
 * as a sentence, so its words are chosen in the language shown when it is
 * read; the language switch of the sign-in page is offered here too.
 *
 * "Send a new code" (W2.5) asks POST /v1/auth/resend-verification for the
 * address the person signed up with (handed over by the sign-up page, or
 * typed here when they arrive from the email's link). The API answers the
 * same for every address, so the confirmation says what happens in each
 * case without knowing which one this is; the codes sent before stop
 * working. A refusal for asking too often is told apart by its code
 * (RATE_LIMITED), never by its English.
 */
import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Field, Input, PageHeader, Badge } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { ApiError, post } from "@/lib/api/client";
import { useTranslation } from "@/lib/i18n";
import { SIGN_UP_LIMITS } from "@/lib/i18n/errors";
import { LanguageSwitch } from "./LanguageSwitch";

type ResendState = "idle" | "sending" | "sent" | "limited" | "error";

/** The address the sign-up page handed over in the navigation state. */
function signedUpEmail(state: unknown): string {
  const email = (state as { email?: unknown } | null)?.email;
  return typeof email === "string" ? email : "";
}

export function VerifyEmailPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [code, setCode] = useState(params.get("code") ?? "");
  const [status, setStatus] = useState<"idle" | "verifying" | "done" | "error">(
    "idle",
  );
  const [error, setError] = useState<unknown>(null);
  const [email, setEmail] = useState(() => signedUpEmail(location.state));
  const [resend, setResend] = useState<ResendState>("idle");
  const [resendError, setResendError] = useState<unknown>(null);
  const [sentTo, setSentTo] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("verifying");
    setError(null);
    try {
      await post("/auth/verify-email", { code });
      setStatus("done");
      // Verification deliberately does NOT return an access token: a six-digit
      // code arriving by email must not be enough to open a session on its own
      // — the password is still required. So the next step is sign-in, not
      // workspace creation. Going straight to /create-workspace sent an
      // unauthenticated POST /v1/tenants, which the API answered with a bare
      // 401. Sign-in then routes to /create-workspace by itself for an account
      // that belongs to no workspace yet.
      setTimeout(() => navigate("/login"), 900);
    } catch (err) {
      setStatus("error");
      setError(err ?? new Error("Verification failed"));
    }
  };

  const requestNewCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setResend("sending");
    setResendError(null);
    try {
      await post("/auth/resend-verification", {
        email,
        locale: i18n.language,
      });
      setSentTo(email);
      setResend("sent");
    } catch (err) {
      if (err instanceof ApiError && err.code === "RATE_LIMITED") {
        setResend("limited");
        return;
      }
      setResend("error");
      setResendError(err ?? new Error("Could not send a new code"));
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ inlineSize: 420, maxInlineSize: "100%" }}>
        <Card>
          <PageHeader
            title={t("auth.verify.title")}
            subtitle={t("auth.verify.subtitle", {
              digits: SIGN_UP_LIMITS.codeDigits,
            })}
          />
          {status === "done" && (
            <div role="status" style={{ marginBlockEnd: 12 }}>
              <Badge tone="success">{t("auth.verify.done")}</Badge>
            </div>
          )}
          {status === "error" && (
            <div
              role="alert"
              style={{
                color: "var(--atlas-danger)",
                fontSize: 13,
                marginBlockEnd: 12,
              }}
            >
              <ErrorText error={error} />
            </div>
          )}
          <form onSubmit={submit}>
            <Field label={t("auth.verify.code")} required>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                autoComplete="one-time-code"
                dir="ltr"
                required
              />
            </Field>
            <Button
              type="submit"
              variant="primary"
              disabled={status === "verifying"}
              style={{ inlineSize: "100%" }}
            >
              {status === "verifying"
                ? t("auth.verify.submitting")
                : t("auth.verify.submit")}
            </Button>
          </form>

          <form
            onSubmit={requestNewCode}
            aria-labelledby="verify-resend-title"
            style={{
              marginBlockStart: 24,
              paddingBlockStart: 16,
              borderBlockStart: "1px solid var(--atlas-border)",
            }}
          >
            <h2
              id="verify-resend-title"
              style={{ fontSize: 15, margin: "0 0 4px 0" }}
            >
              {t("auth.verify.resendTitle")}
            </h2>
            <p
              className="atlas-muted"
              style={{ fontSize: 13, margin: "0 0 12px 0" }}
            >
              {t("auth.verify.resendHint")}
            </p>
            <Field label={t("auth.verify.resendEmail")} required>
              <Input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (resend !== "sending") setResend("idle");
                }}
                autoComplete="email"
                dir="ltr"
                required
              />
            </Field>
            {resend === "sent" && (
              <div role="status" style={{ fontSize: 13, marginBlockEnd: 12 }}>
                <Badge tone="success">{t("auth.verify.resendSentBadge")}</Badge>{" "}
                {t("auth.verify.resendSent", { email: sentTo })}
              </div>
            )}
            {resend === "limited" && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlockEnd: 12,
                }}
              >
                {t("auth.verify.resendLimited")}
              </div>
            )}
            {resend === "error" && (
              <div
                role="alert"
                style={{
                  color: "var(--atlas-danger)",
                  fontSize: 13,
                  marginBlockEnd: 12,
                }}
              >
                <ErrorText error={resendError} />
              </div>
            )}
            <Button
              type="submit"
              disabled={resend === "sending"}
              style={{ inlineSize: "100%" }}
            >
              {resend === "sending"
                ? t("auth.verify.resendSending")
                : resend === "error"
                  ? t("auth.verify.resendRetry")
                  : t("auth.verify.resendSubmit")}
            </Button>
          </form>
          <div style={{ marginBlockStart: 16, fontSize: 13 }}>
            <LanguageSwitch />
          </div>
        </Card>
      </div>
    </div>
  );
}
